import React from 'react';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';
import { Card, Typography, Tooltip, Tabs, Alert, Button, message, Row, Col, Divider, Space, Switch } from 'antd';
import { DownloadOutlined, FileTextOutlined, ApartmentOutlined, UnorderedListOutlined, BgColorsOutlined } from '@ant-design/icons';
import D3PhylogeneticTree from './D3PhylogeneticTree';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

// 氨基酸颜色映射 (ClustalX color scheme)
const aminoAcidColors: Record<string, string> = {
  A: '#8888FF', G: '#8888FF', P: '#8888FF', S: '#8888FF', T: '#8888FF', // Small
  H: '#80A0F0', K: '#80A0F0', R: '#80A0F0', // Positive
  D: '#F08080', E: '#F08080', // Negative
  F: '#C0C000', W: '#C0C000', Y: '#C0C000', // Aromatic
  C: '#F09048', M: '#F09048', // Sulfur
  I: '#00FF00', L: '#00FF00', V: '#00FF00', // Hydrophobic
  N: '#FF7F7F', Q: '#FF7F7F', // Amide
  '-': '#FFFFFF',
};

// 核苷酸颜色映射
const nucleotideColors: Record<string, string> = {
  A: '#A0A0FF', // 蓝色
  T: '#FF8080', // 红色
  U: '#FF8080', // 红色 (RNA)
  G: '#FF8080', // 红色
  C: '#80FF80', // 绿色
  '-': '#FFFFFF', // 空位为白色
};

// 序列记录类型定义
interface SequenceRecord {
  id: string;
  sequence: string;
}

const MultipleSequenceAlignmentResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults,
}) => {
  const [selectedTab, setSelectedTab] = React.useState<string>('1');
  const [singleLineMode, setSingleLineMode] = React.useState<boolean>(true);
  const [onlyHighlightDifferences, setOnlyHighlightDifferences] = React.useState<boolean>(false);
  
  // 处理数据格式，兼容旧版数据
  const hasAlignment = data?.aligned_sequences && Array.isArray(data.aligned_sequences) && data.aligned_sequences.length > 0;
  const hasNames = data?.names && Array.isArray(data.names) && data.names.length > 0;
  const hasNewickTree = data?.newick_tree && typeof data.newick_tree === 'string';
  const hasPhyloXMLTree = data?.phyloxml_tree && typeof data.phyloxml_tree === 'string';
  const sequenceType = data?.sequence_type || 'protein'; // 默认为蛋白质序列

  // 准备比对数据
  const prepareAlignmentData = (): SequenceRecord[] => {
    if (!hasAlignment || !hasNames) return [];
    
    return data.aligned_sequences.map((seq: string, index: number) => {
      const name = hasNames && index < data.names.length ? data.names[index].replace('>', '') : `seq${index+1}`;
      return {
        id: name,
        sequence: seq
      };
    });
  };
  
  const alignmentData = prepareAlignmentData();
  
  // 下载处理函数
  const handleDownload = (fileType: 'fasta' | 'newick' | 'phyloxml') => {
    let downloadUrl = '';
    let filename = '';
    
    // 构建下载URL
    if (fileType === 'fasta' && hasAlignment) {
      // 直接在前端生成FASTA格式文件
      const fastaContent = alignmentData.map((item: SequenceRecord) => `>${item.id}\n${item.sequence}`).join('\n');
      const blob = new Blob([fastaContent], { type: 'text/plain' });
      downloadUrl = URL.createObjectURL(blob);
      filename = 'aligned_sequences.fasta';
    } else if (fileType === 'newick' && hasNewickTree) {
      // 直接在前端生成Newick格式文件
      const blob = new Blob([data.newick_tree], { type: 'text/plain' });
      downloadUrl = URL.createObjectURL(blob);
      filename = 'phylogenetic_tree.nwk';
    } else if (fileType === 'phyloxml' && hasPhyloXMLTree) {
      // 直接在前端生成PhyloXML格式文件
      const blob = new Blob([data.phyloxml_tree], { type: 'text/plain' });
      downloadUrl = URL.createObjectURL(blob);
      filename = 'phylogenetic_tree.xml';
    } else {
      message.error(`没有可用的${fileType}格式数据可供下载`);
      return;
    }
    
    // 执行下载
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 释放URL对象
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
    
    message.success(`${filename}下载成功`);
  };

  // 检查是否有进化树数据
  const hasTreeData = data?.newick_string || data?.newick_tree || data?.phyloxml_tree;

  // 没有数据时的提示
  if (!hasAlignment && !hasNewickTree) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "多序列比对"}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
        fullWidth={true}
      >
        <Alert 
          message="无可显示数据" 
          description="无多序列比对或进化树数据可显示。" 
          type="warning" 
          showIcon 
        />
      </BaseResultDisplay>
    );
  }

  // 渲染序列比对结果
  const renderAlignment = () => {
    // 处理每一列的颜色标识
    const getColorScheme = (char: string, charIndex: number) => {
      // 如果启用了"只标记不一致"模式，检查该位置是否所有序列都相同
      if (onlyHighlightDifferences) {
        // 检查该位置是否所有序列都相同
        const allSame = alignmentData.every((record) => 
          record.sequence[charIndex] === alignmentData[0].sequence[charIndex]
        );
        
        if (allSame) {
          return 'transparent'; // 如果所有序列该位置都相同，不标记颜色
        }
      }
      
      // 根据序列类型选择颜色方案
      if (sequenceType === 'protein') {
        return aminoAcidColors[char.toUpperCase()] || 'transparent';
      } else {
        return nucleotideColors[char.toUpperCase()] || 'transparent';
      }
    };

    // 渲染单行模式的序列
    const renderSingleLineMode = (record: SequenceRecord, index: number) => (
      <div key={index} style={{ marginBottom: '8px', display: 'flex' }}>
        <Text strong style={{ 
          minWidth: '150px', 
          marginRight: '10px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {record.id}
        </Text>
        <div style={{ flex: 1 }}>
          {record.sequence.split('').map((char: string, charIndex: number) => (
            <Tooltip key={charIndex} title={`${record.id}: ${char} at ${charIndex + 1}`}>
              <span style={{ 
                backgroundColor: getColorScheme(char, charIndex), 
                padding: '1px 0',
                display: 'inline-block',
                width: '10px',
                textAlign: 'center',
                color: char === '-' ? '#aaa' : '#000'
              }}>
                {char}
              </span>
            </Tooltip>
          ))}
        </div>
      </div>
    );

    // 渲染自适应换行模式的序列
    const renderWrappedMode = (record: SequenceRecord, index: number) => {
      // 将序列分成多个片段，每个片段占据一行
      const chunks: string[] = [];
      const chunkSize = 60; // 假设每行显示60个字符
      
      for (let i = 0; i < record.sequence.length; i += chunkSize) {
        chunks.push(record.sequence.substring(i, i + chunkSize));
      }

      return (
        <div key={index} style={{ marginBottom: '16px' }}>
          <Text strong>{record.id}</Text>
          {chunks.map((chunk, chunkIndex) => (
            <div key={chunkIndex} style={{ marginTop: '4px' }}>
              {chunk.split('').map((char: string, charIndex: number) => {
                const globalCharIndex = chunkIndex * chunkSize + charIndex;
                return (
                  <Tooltip key={charIndex} title={`${record.id}: ${char} at ${globalCharIndex + 1}`}>
                    <span style={{ 
                      backgroundColor: getColorScheme(char, globalCharIndex), 
                      padding: '1px 0',
                      display: 'inline-block',
                      width: '10px',
                      textAlign: 'center',
                      color: char === '-' ? '#aaa' : '#000'
                    }}>
                      {char}
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>
      );
    };

    // 渲染按块分组的序列（新的显示方式）
    const renderBlockMode = () => {
      if (!alignmentData || !alignmentData.length) return null;

      const seqLength = alignmentData[0].sequence.length;
      const blockSize = 60; // 每块显示的字符数
      const totalBlocks = Math.ceil(seqLength / blockSize);
      const blocks = [];

      // 创建所有的块
      for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex++) {
        const startPos = blockIndex * blockSize;
        const endPos = Math.min((blockIndex + 1) * blockSize, seqLength);
        
        // 创建一个块，包含所有序列在这个范围内的片段
        const block = (
          <div key={`block-${blockIndex}`} style={{ marginBottom: '20px' }}>
            <Text type="secondary">位置: {startPos + 1}-{endPos}</Text>
            {alignmentData.map((record: SequenceRecord, seqIndex: number) => {
              const chunk = record.sequence.substring(startPos, endPos);
              return (
                <div key={`seq-${seqIndex}-block-${blockIndex}`} style={{ marginBottom: '8px', display: 'flex' }}>
                  <Text strong style={{ 
                    minWidth: '150px', 
                    marginRight: '10px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {record.id}
                  </Text>
                  <div>
                    {chunk.split('').map((char: string, charIndex: number) => {
                      const globalCharIndex = startPos + charIndex;
                      return (
                        <Tooltip key={charIndex} title={`${record.id}: ${char} at ${globalCharIndex + 1}`}>
                          <span style={{ 
                            backgroundColor: getColorScheme(char, globalCharIndex), 
                            padding: '1px 0',
                            display: 'inline-block',
                            width: '10px',
                            textAlign: 'center',
                            color: char === '-' ? '#aaa' : '#000'
                          }}>
                            {char}
                          </span>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
        
        blocks.push(block);
      }

      return <div>{blocks}</div>;
    };
    
    return (
      <div>
        <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={() => handleDownload('fasta')}
              >
                下载FASTA格式比对结果
              </Button>
            </Col>
            <Col>
              <Space>
                <Text>显示方式：</Text>
                <Switch
                  checkedChildren={<UnorderedListOutlined />}
                  unCheckedChildren={<BgColorsOutlined />}
                  checked={singleLineMode}
                  onChange={(checked) => setSingleLineMode(checked)}
                />
                <Text>{singleLineMode ? '单行显示' : '换行显示'}</Text>
                <Divider type="vertical" />
                <Text>颜色标记：</Text>
                <Switch
                  checkedChildren="仅差异"
                  unCheckedChildren="全部"
                  checked={onlyHighlightDifferences}
                  onChange={(checked) => setOnlyHighlightDifferences(checked)}
                />
                <Text>{onlyHighlightDifferences ? '仅差异处着色' : '全部着色'}</Text>
              </Space>
            </Col>
          </Row>
          <Text type="secondary">序列总数: {alignmentData.length}, 序列长度: {alignmentData[0]?.sequence.length || 0}</Text>
        </Space>
        
        <div style={{ 
          fontFamily: 'monospace', 
          whiteSpace: 'pre', 
          overflowX: 'auto', 
          background: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '4px',
          border: '1px solid #e8e8e8',
          maxHeight: '600px',
          overflowY: 'auto',
          width: '100%',
          maxWidth: '100%' // 确保宽度可以尽量扩展
        }}>
          {singleLineMode 
            ? alignmentData.map((record: SequenceRecord, index: number) => renderSingleLineMode(record, index))
            : renderBlockMode()
          }
        </div>
      </div>
    );
  };
  
  // 渲染进化树
  const renderTree = () => {
    // 处理分类数据
    const taxonomyData: Record<string, string> = {};
    
    // 从后端数据中提取分类信息
    if (data?.taxonomy_data && typeof data.taxonomy_data === 'object') {
      Object.assign(taxonomyData, data.taxonomy_data);
    } else {
      // 尝试从其他字段提取分类信息
      
      // 如果有序列注释，尝试从中提取分类信息
      if (data?.sequence_annotations && Array.isArray(data.sequence_annotations)) {
        data.sequence_annotations.forEach((annotation: any) => {
          if (annotation.id && annotation.taxonomy) {
            taxonomyData[annotation.id] = annotation.taxonomy;
          }
        });
      }
      
      // 如果有序列元数据，尝试从中提取分类信息
      if (data?.sequences_metadata && typeof data.sequences_metadata === 'object') {
        Object.entries(data.sequences_metadata).forEach(([id, metadata]: [string, any]) => {
          if (metadata.taxonomy) {
            taxonomyData[id] = metadata.taxonomy;
          }
        });
      }
      
      // 如果有序列名称和分类信息，尝试匹配
      if (hasNames && data?.taxonomy && Array.isArray(data.taxonomy)) {
        data.names.forEach((name: string, index: number) => {
          const cleanName = name.replace('>', '');
          if (index < data.taxonomy.length) {
            taxonomyData[cleanName] = data.taxonomy[index];
          }
        });
      }
    }
    
    return (
      <div>
        <Space direction="vertical" style={{ width: '100%', marginBottom: '16px' }}>
          <Space>
            {hasNewickTree && (
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={() => handleDownload('newick')}
              >
                下载Newick格式
              </Button>
            )}
            {hasPhyloXMLTree && (
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={() => handleDownload('phyloxml')}
              >
                下载PhyloXML格式
              </Button>
            )}
          </Space>
          <Text type="secondary">使用D3.js库从头实现的进化树可视化，支持多种布局方式和交互功能。</Text>
        </Space>
        
        <div style={{ border: '1px solid #f0f0f0', borderRadius: '4px', overflow: 'auto' }}>
          <D3PhylogeneticTree
            newickString={data?.newick_string || data?.newick_tree || ''}
            phyloXmlString={data?.phyloxml_tree || ''}
            // taxonomyData={taxonomyData}
            width={800}
            height={600}
            showControls={true}
          />
        </div>
      </div>
    );
  };

  // 构建标签页
  const items = [
    {
      label: (
        <span>
          <FileTextOutlined /> 序列比对
        </span>
      ),
      key: '1',
      children: hasAlignment ? renderAlignment() : <Alert message="无序列比对数据" type="warning" />,
    },
    {
      label: (
        <span>
          <ApartmentOutlined /> 进化树可视化
        </span>
      ),
      key: '2',
      children: hasTreeData ? renderTree() : <Alert message="无进化树数据" type="warning" />,
    }
  ];

  return (
    <BaseResultDisplay
      data={data}
      title={title || "多序列比对结果"}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
      fullWidth={true}
    >
      <div style={{ width: '100%', overflow: 'auto' }}>
        <Tabs 
          activeKey={selectedTab} 
          onChange={setSelectedTab}
          items={items}
          type="card"
          size="large"
          style={{ marginBottom: '16px' }}
        />
      </div>
    </BaseResultDisplay>
  );
};

export default MultipleSequenceAlignmentResult;